import React, { useState, useEffect, memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import Breadcrumb from '../components/breadcrumb';
import { JobsiteDetailsSkeleton } from '../components/Skeleton';
import JobsiteVisitsSection from '../components/jobsite-details/JobsiteVisitsSection';
import { JobsiteVisitsService } from '../services/jobsite-visits.service';
import { JobsiteVisitResponse } from '../types/jobsite-visits.types';
import { JobsiteService } from '../services/jobsite.service';
import { getJobsiteCompletionPercentage } from '../utils/jobsiteUtils';
import StatusPill from '../components/StatusPill';

// Navigation Component
const Navigation = memo(({ onBack }: { onBack: () => void }) => (
  <div className="mb-6 flex items-center">
    <ArrowLeft
      className="text-gray-600 mr-2 w-5 h-5 cursor-pointer"
      onClick={onBack}
      id="back-button"
    />
    <Breadcrumb title="Jobsite Visit Details" />
  </div>
));

const JobsiteVisitDetails: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const visitId = params.visitId;

  // State Management
  const [loading, setLoading] = useState(true);
  const [visitData, setVisitData] = useState<JobsiteVisitResponse | null>(null);
  const [formattedAddress, setFormattedAddress] = useState<string>('');
  // Load jobsite visit data
  useEffect(() => {
    const loadVisitData = async () => {
      if (!visitId) {
        toast.error('Visit ID is required');
        navigate(-1);
        return;
      }

      try {
        setLoading(true);
        const data = await JobsiteVisitsService.getJobsiteVisitById(visitId);
        const addressResponse = await JobsiteService.getJobsiteAddress(data.jobId.id);

        if (!addressResponse?.address1) return '';

        const parts = [
          addressResponse.address1,
          addressResponse.address2,
          addressResponse.city,
          addressResponse.postalCode
        ].filter(Boolean);

        const formattedAddress = parts.join(', ');

        setVisitData(data);
        setFormattedAddress(formattedAddress);
      } catch (error) {
        console.error('Error loading visit data:', error);
        toast.error('Failed to load visit data');
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    loadVisitData();
  }, [visitId, navigate]);

  // Navigation Functions
  const handleBack = () => {
    navigate(-1);
  };

  // Helper function for JobsiteVisitsSection props
  const getStatusCompletionPercentage = (status: string): string => {
    return `${getJobsiteCompletionPercentage(status)}`;
  };

  const handleViewFile = (fileId: string) => {
    // Implement file viewing logic here
    toast.info('File viewing functionality not implemented yet');
  };

  // Show loading skeleton while data is loading
  if (loading) {
    return (
      <div className="bg-gray-50 p-3 sm:p-6">
        <Navigation onBack={handleBack} />
        <JobsiteDetailsSkeleton />
      </div>
    );
  }

  // Show error if no visit data
  if (!visitData) {
    return (
      <div className="bg-gray-50 p-3 sm:p-6 min-h-screen">
        <Navigation onBack={handleBack} />
        <div className="bg-white rounded-md shadow p-6 text-center">
          <p className="text-gray-500">Visit not found</p>
        </div>
      </div>
    );
  }

  // Transform visitData to match JobsiteVisitsSection expected props
  const jobsiteVisitDetails = {
    jobCompletionStatus: {
      status: visitData.jobCompletionStatus?.status || 'Start'
    },
    comments: visitData.comments?.map((c: { comment: string }) => c.comment) || []
  };
  const jobsiteData = visitData.jobId;
  const jobDetails = {
    id: visitData.id,
    contractor: {
      name: visitData.contractor?.companyName || visitData.contractor?.firstName + ' ' + visitData.contractor?.lastName || 'N/A'
    },
    siteContact: '',
    siteContactEmail: '',
    jobDetailsSectors: [],
    industry: { name: '' },
    jobCompletionStatus: {
      status: visitData.jobCompletionStatus?.status || 'Start'
    },
    jobSiteName: jobsiteData.jobSiteName,
    subsite: jobsiteData.subsite,
    status: visitData.status,
    createdAt: visitData.createdAt,
    updatedAt: visitData.updatedAt,
    comments: visitData.comments?.map(c => c.comment) || []
  };

  // Extract trades data from visit if available

  const jobsiteTradeData = (visitData as any)?.jobTrades ? {
    id: (visitData as any).jobTrades.id || '',
    noOfTrades: (visitData as any).jobTrades.noOfTrades || '0',
    createdAt: visitData.createdAt,
    updatedAt: visitData.updatedAt,
    jobTrade: (visitData as any).jobTrades.jobTrade || []
  } : null;

  const jobsiteFiles = visitData.jobFiles || [];

  return (
    <div className="bg-gray-50 p-3 sm:p-6 min-h-screen">
      <Navigation onBack={handleBack} />

      {/* Visit Info Header */}
      <div className="bg-white rounded-md shadow mb-4">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-[24px] font-[700] mb-1">
                {jobDetails?.jobSiteName || 'Jobsite Visit'}
              </h1>
              {jobDetails?.subsite && (
                <p className="text-sm text-gray-500">{jobDetails.subsite}</p>
              )}
            </div>

            <StatusPill status={visitData.status} size="md" />
          </div>

          {/* Visit Metadata */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <p className='text-gray-700 text-sm sm:text-base'>
              <span className='text-[16px] font-[700] text-black'>Address</span>
              : {formattedAddress || 'No address has been added.'}
            </p>
          </div>
        </div>
      </div>

      {/* Visit Details using JobsiteVisitsSection */}
      <JobsiteVisitsSection
        title="Visit Details"
        visitDate={visitData.visitDate}
        totalWorkers={visitData.totalWorkers}
        jobsiteVisitDetails={jobsiteVisitDetails}
        jobDetails={jobDetails}
        getCompletionPercentage={getJobsiteCompletionPercentage}
        getStatusCompletionPercentage={getStatusCompletionPercentage}
        jobsiteFiles={jobsiteFiles}
        jobsiteTradeData={jobsiteTradeData}
        handleViewFile={handleViewFile}
      />
    </div>
  );
};

export default JobsiteVisitDetails;